import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BillingCycleModalComponent } from '../../modals/billing-cycle-modal/billing-cycle-modal.component';
import { IBillingCycle, IProduct, ProductsSubprojectService } from '../../../../services/products-subproject/products-subproject.service';

@Component({
  selector: 'app-vendor-products-stripe-products',
  templateUrl: './vendor-products-stripe-products.component.html',
  styleUrls: ['./vendor-products-stripe-products.component.scss'],
})
export class VendorProductsStripeProductsComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;

  public searchValue = '';
  public billingCycles: IBillingCycle[] = [];
  public tableDataSource = new MatTableDataSource<IProduct>();
  public columnNames = ['active', 'name', 'price', 'billingCycle', 'billingDay', 'actions'];
  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;

  constructor(
    private productsSubprojectService: ProductsSubprojectService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe((value: string) => this.applyFilterValue(value));
  }

  async ngOnInit(): Promise<void> {
    await this.loadBillingCycles();
    await this.loadProducts();
    this.tableDataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  async loadProducts(): Promise<void> {
    const response = await this.productsSubprojectService.getActiveProducts();
    if ('success' in response && response.success) {
      this.tableDataSource.data = response.data;
    } else {
      this.snackBar.open('Failed to fetch products', 'Close', { duration: 3000 });
    }
  }

  async loadBillingCycles(): Promise<void> {
    const response = await this.productsSubprojectService.getBillingCycles();
    if (response.success) {
      this.billingCycles = response.data;
    } else {
      this.snackBar.open('Failed to fetch billing cycles', 'Close', { duration: 3000 });
    }
  }

  queueFilter(value: string): void {
    this.searchValueChanges.next(value);
  }

  clearSearch(): void {
    this.searchValue = '';
    this.queueFilter('');
  }

  getBillingCycleName(id: number | undefined): string {
    if (!id) {
      return 'N/A';
    }
    const cycle = this.billingCycles.find((item) => item.id === id);
    return cycle ? cycle.type : 'N/A';
  }

  getFixedBillingId(): number | null {
    if (!this.billingCycles.length) {
      return null;
    }
    const fixed = this.billingCycles.find((item) => item.type === 'fixedBilling');
    return fixed?.id ?? null;
  }

  openChangeCycleModal(product: IProduct): void {
    const dialogRef = this.dialog.open(BillingCycleModalComponent, {
      width: '480px',
      data: {
        resourceId: product.id,
        productId: product.id,
        billingCycleInfo: {
          billingCycleId: product.billingCycleId,
          billingDay: product.billingDay,
        },
        billingCycles: this.billingCycles,
        saveFunction: async (payload: { productId: string; billingCycleId: number | null; billingDay?: number }) => {
          const { productId, billingCycleId, billingDay } = payload;
          await this.productsSubprojectService.updateProduct(productId, {
            billingCycleId: billingCycleId ?? undefined,
            billingDay,
          });
        },
      },
    });
    dialogRef.afterClosed().subscribe((changed) => {
      if (changed) {
        this.loadProducts();
      }
    });
  }

  openChangeDayModal(product: IProduct): void {
    this.openChangeCycleModal(product);
  }

  getPrice(product: IProduct): string {
    const price = (product as any).price;
    if (price === undefined || price === null) {
      return '--';
    }
    return `${price}`;
  }

  private applyFilterValue(value: string): void {
    this.tableDataSource.filter = value.trim().toLowerCase();
  }
}
